import { useEffect, useState } from "react";
import confetti from "canvas-confetti";
import { api } from "../lib/api";
import { Flame, X as XIcon } from "@phosphor-icons/react";
import { Button } from "./ui/button";

const PALETTE = ["#3B5249", "#C86A4F", "#8AB09E", "#E28B72", "#EAE6DF"];

function burst() {
  const end = Date.now() + 900;
  const shoot = () => {
    confetti({
      particleCount: 5, angle: 60, spread: 65, origin: { x: 0, y: 0.7 },
      colors: PALETTE, scalar: 0.9, ticks: 200,
    });
    confetti({
      particleCount: 5, angle: 120, spread: 65, origin: { x: 1, y: 0.7 },
      colors: PALETTE, scalar: 0.9, ticks: 200,
    });
    if (Date.now() < end) requestAnimationFrame(shoot);
  };
  confetti({
    particleCount: 80, spread: 80, origin: { y: 0.6 },
    colors: PALETTE, ticks: 220, scalar: 1.1,
  });
  shoot();
}

const MESSAGES = [
  "Another day, kept.",
  "Today is on record.",
  "One more page filled.",
  "That's today, written.",
  "Small ritual, real magic.",
  "Nothing lost from today.",
];

export default function CelebrationModal({ open, onClose, firstName = "you" }) {
  const [streak, setStreak] = useState(null);
  const [messageIdx] = useState(() => Math.floor(Math.random() * MESSAGES.length));

  useEffect(() => {
    if (!open) return;
    burst();
    api.get("/entries/stats/streak")
      .then((r) => setStreak(r.data))
      .catch(() => setStreak(null));
    const t = setTimeout(() => onClose?.(), 6500);
    return () => clearTimeout(t);
  }, [open, onClose]);

  if (!open) return null;

  const current = streak?.current_streak ?? 0;
  const best = streak?.longest_streak ?? 0;
  const isBest = current > 0 && current >= best;

  return (
    <div
      data-testid="celebration-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-background/70 backdrop-blur-md p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-full max-w-md bg-card border border-border rounded-lg shadow-2xl p-8 sm:p-10 fade-up">
        <button
          data-testid="celebration-close"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <XIcon size={16} />
        </button>

        <p className="text-xs uppercase tracking-[0.35em] text-accent">
          Thank you, {firstName}
        </p>

        <h2
          data-testid="celebration-headline"
          className="font-serif text-5xl sm:text-6xl font-light tracking-tighter mt-4 leading-none"
        >
          {MESSAGES[messageIdx].split(",")[0]},<br />
          <span className="italic text-accent">
            {MESSAGES[messageIdx].split(",").slice(1).join(",").trim() || "well done"}.
          </span>
        </h2>

        <p className="mt-6 text-sm text-muted-foreground leading-relaxed">
          A minute a day is how a year of memory gets kept. See you tomorrow?
        </p>

        {current > 0 && (
          <div
            data-testid="celebration-streak"
            className="mt-8 flex items-center justify-between border-t border-border pt-6"
          >
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-full bg-accent/20 text-accent flex items-center justify-center">
                <Flame size={20} weight="fill" />
              </div>
              <div>
                <p className="font-serif text-3xl leading-none">
                  {current} <span className="text-base text-muted-foreground">day{current > 1 ? "s" : ""}</span>
                </p>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-1">
                  {isBest && current > 1 ? "Personal best" : "In a row"}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Best</p>
              <p className="font-serif text-2xl mt-1">{best}</p>
            </div>
          </div>
        )}

        <Button
          data-testid="celebration-done-btn"
          onClick={onClose}
          className="w-full mt-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-11"
        >
          Close
        </Button>
      </div>
    </div>
  );
}
